import { useParams, Link } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useFestival } from '../hooks/useFestival'
import { SvgLogo } from '../components/SvgLogo'
import { withErrorCode } from '@/lib/errors'

export function FestivalPage() {
  const { slug } = useParams<{ slug: string }>()
  const { festival, editions, loading, error } = useFestival(slug ?? '')

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-24 w-full max-w-2xl" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (error || !festival) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-muted-foreground">
          {withErrorCode('Festival not found.', 'festivals/not-found')}
        </p>
        <Button asChild variant="outline" className="mt-6">
          <Link to="/festivals">Back to Festivals</Link>
        </Button>
      </div>
    )
  }

  return (
    <div>
      {/* Hero image */}
      {festival.heroUrl && (
        <div className="relative h-64 w-full overflow-hidden sm:h-80 lg:h-96">
          <img
            src={festival.heroUrl}
            alt={festival.name}
            className="h-full w-full object-cover"
            style={{
              objectPosition: `${(festival.heroFocusX ?? 0.5) * 100}% ${(festival.heroFocusY ?? 0.5) * 100}%`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        </div>
      )}

      <div className="container mx-auto px-4 py-16">
        {/* Festival header */}
        <div className="mb-12 max-w-2xl">
          <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-orange">
            <Link to="/festivals" className="hover:underline">Festivals</Link>
            {' · '}
            {festival.name}
          </p>
          <div className="mb-2">
            {festival.logoUrl ? (
              <SvgLogo
                url={festival.logoUrl}
                alt={`${festival.name} logo`}
                className="mb-3 block h-36 w-auto max-w-sm"
              />
            ) : (
              <h1 className="mb-2 text-4xl font-bold tracking-tight">{festival.name}</h1>
            )}
          </div>
          {(festival.showTagline ?? true) && (
            <p className={`mb-4 text-sm font-medium uppercase tracking-widest ${festival.logoUrl ? 'text-brand-orange' : 'text-muted-foreground'}`}>
              {festival.tagline}
            </p>
          )}
          <p className="text-muted-foreground leading-relaxed">{festival.description}</p>
        </div>

      {/* Applications CTA */}
      <div className="mb-14 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button asChild>
          <Link to="/applications">View Open Applications</Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/connect">Stay Updated</Link>
        </Button>
      </div>

      {/* Past Editions */}
      {editions.length > 0 && (
        <section>
          <h2 className="mb-6 text-2xl font-bold">Past Editions</h2>
          <Tabs defaultValue={String(editions[0]?.year)}>
            <TabsList className="mb-8 h-auto flex-wrap gap-1">
              {editions.map((edition) => (
                <TabsTrigger key={edition.id} value={String(edition.year)}>
                  {festival.name} '{String(edition.year).slice(-2)}
                </TabsTrigger>
              ))}
            </TabsList>

            {editions.map((edition) => (
              <TabsContent key={edition.id} value={String(edition.year)}>
                <ul className="columns-1 gap-4 sm:columns-2 lg:columns-3">
                  {edition.participants.map((name) => (
                    <li
                      key={name}
                      className="mb-2 break-inside-avoid text-sm text-muted-foreground leading-relaxed"
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              </TabsContent>
            ))}
          </Tabs>
        </section>
      )}
      </div>
    </div>
  )
}
